/* eslint-disable react/no-array-index-key */
import { Children, useState, forwardRef, useContext } from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeContext } from 'styled-components';
import { useDispatch } from 'react-redux';
import IconButton from '@base/IconButton/IconButton';
import H4 from '@base/H4/H4';
import Grow from '@base/Grow/Grow';
import Link from '@base/Link/Link';
import P1 from '@base/P1/P1';
import ChevronLeft from '@icon/ChevronLeft';
import ChevronRight from '@icon/ChevronRight';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import analyticsAction from '@actions/analytics';
import Button from '@base/Button/Button';
import useWindowSize from '@hooks/useWindowSize';
import { Spacings, Fonts } from '@styles/index';
import pushRoute from '@utils/pushRoute';

const StyH4 = styled(H4)`
  color: ${({ theme }) => theme.colors.fontGrey};
`;

const BoldP1 = styled(P1)`
  font-weight: ${Fonts.BOLD};
`;

const ItemWrap = styled.div`
  grid-column: ${({ gridColumn }) => gridColumn};
  opacity: ${({ opacity }) => opacity};
  display: flex;
  transition: opacity 1000ms ease-in, transform 400ms ease-in !important;
  @media (max-width: 47.95em) {
    opacity: 1;
  }
`;

const CarouselWrapper = styled.div`
  grid-column: ${({ gridColumn }) => gridColumn};
  position: relative;
  width: calc(100% + ${Spacings.SPACING_8B});
  margin-left: -${Spacings.SPACING_4B};
  margin-right: -${Spacings.SPACING_4B};
  @media (max-width: 47.95em) {
    margin-left: 0;
    margin-right: 0px;
    width: 100%;
    ${({ $css }) => $css || ''}
  }
`;

const CardContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(600, 32px);
  grid-column-gap: ${Spacings.SPACING_4B};
  overflow: hidden;
  padding-left: ${Spacings.SPACING_4B};
  padding-bottom: ${({ $showBottomGap }) =>
    $showBottomGap ? Spacings.SPACING_9B : '0px'};
  & > a,
  & > div {
    transform: ${({ pos }) =>
      (pos && `translate3d(calc(${-100 * pos}% - ${pos * 16}px), 0, 0)`) ||
      'initial'};
    transition: transform 400ms ease-in;
  }
  @media (max-width: 47.95em) {
    padding: 0 0 0 16px;
    overflow: scroll;
    overscroll-behavior: unset;
    scroll-behavior: smooth;
    ::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

const ControlButton = styled(IconButton)`
  position: absolute;
  visibility: hidden;
  opacity: 0;
  transition: visibility 0s linear 300ms, opacity 300ms;
  top: calc(50% - 16px - ${({ $topAdjustment }) => $topAdjustment});
  box-shadow: ${({ theme }) => boxShadowGenerator({ theme })};
  ${CarouselWrapper}:hover & {
    visibility: visible;
    opacity: 1;
    transition: visibility 0s linear 0s, opacity 300ms;
  }
  ${({ direction }) => (direction === 'prev' ? 'left' : 'right')}: 0px;
  @media (max-width: 47.95em) {
    display: none;
  }
`;

const CaraouselInfo = styled.div`
  display: flex;
  margin-bottom: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_7B};
  align-items: center;
  padding: 0;
  grid-column: span 18;
  @media (max-width: 70.95em) {
    margin: 0 16px 24px 16px;
  }
`;

const NavButtonsContainer = styled.div`
  grid-column: ${({ $gridColumn }) => $gridColumn};
  padding: ${Spacings.SPACING_2B};
  padding-bottom: ${Spacings.SPACING_2B};
  display: flex;
  justify-content: center;
  margin-top: -${Spacings.SPACING_9B};
`;

const NavButton = styled.div`
  width: ${Spacings.SPACING_2B};
  height: ${Spacings.SPACING_2B};
  border-radius: 50%;
  background-color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.colors.fontDarkBlueV3 : theme.colors.fontLightBlueV2};
  margin: 0 ${Spacings.SPACING_1B};
  cursor: pointer;
`;

const SeeAll = styled(Button)`
  display: none;
  @media (max-width: 47.95em) {
    display: block;
    min-height: 40px;
    height: 40px;
    width: calc(100% - 32px);
    background: #fff;
    border: 1px solid #f0f4f7;
    border-radius: 8px;
    color: #08bd80;
    font-size: 14px;
    margin: 16px 16px 56px;
  }
`;

const Carousel = forwardRef(
  (
    {
      children,
      label,
      seeAllLink,
      href,
      showControls,
      className,
      numberOfItems,
      gridColumn,
      showNavButtons,
      dataAnalytics,
      showLabel,
      viewRef,
      showInfo,
      showBottomGap,
      $css
    },
    ref
  ) => {
    const { isMobile } = useWindowSize();
    const dispatch = useDispatch();
    const theme = useContext(ThemeContext);
    const items = Children.toArray(children);
    const [pos, setPos] = useState(0);
    const showNavigationButtons = showNavButtons && items.length > 1;

    const finalItems = items.map((item, index) => {
      const gridColumnForItem = item.props.gridColumn || 'span 6';
      const endPoint = pos + (numberOfItems - 1);
      const startPoint = pos;
      const opacity = index >= startPoint && index <= endPoint ? 1 : 0;
      return (
        <ItemWrap gridColumn={gridColumnForItem} opacity={opacity} key={index}>
          {item}
        </ItemWrap>
      );
    });

    const handleNext = () => {
      if (items.length - numberOfItems - 1 >= pos) setPos(pos + 1);
    };
    const handlePrev = () => {
      if (pos > 0) setPos(pos - 1);
    };

    const controlButtons = () => {
      if (!showControls || items.length <= numberOfItems) return null;
      const prevAvailable = pos > 0;
      const nextAvailable = pos < items.length - numberOfItems;
      return (
        <>
          {prevAvailable ? (
            <ControlButton
              onClick={handlePrev}
              direction="prev"
              bgColor={theme.colors.whiteV3}
              hovDisabled
              $topAdjustment={showNavigationButtons ? '18px' : '0px'}
            >
              <ChevronLeft
                size="24px"
                aria-hidden="true"
                color={theme.colors.fontGrey}
              />
            </ControlButton>
          ) : null}
          {nextAvailable ? (
            <ControlButton
              onClick={handleNext}
              direction="next"
              bgColor={theme.colors.whiteV3}
              hovDisabled
              $topAdjustment={showNavigationButtons ? '18px' : '0px'}
            >
              <ChevronRight
                size="24px"
                aria-hidden="true"
                color={theme.colors.fontGrey}
              />
            </ControlButton>
          ) : null}
        </>
      );
    };

    const renderNavButtons = () => {
      const handlenavButtonClick = (position) => () => {
        setPos(position);
      };
      if (showNavigationButtons) {
        const navbuttons = items.map((_, index) => (
          <NavButton
            onClick={handlenavButtonClick(index)}
            key={index}
            $isSelected={index === pos}
          />
        ));
        return (
          <NavButtonsContainer $gridColumn={gridColumn}>
            {navbuttons}
          </NavButtonsContainer>
        );
      }
      return null;
    };

    const seeAllAnalytics = () => {
      dispatch(
        analyticsAction(
          `${dataAnalytics}-seeAll`,
          '',
          {},
          { 'Last Primary Source Section': label }
        )
      );
    };

    const onClickSeeAll = () => {
      pushRoute(href, seeAllLink);
    };

    return (
      <>
        {showInfo && (
          <CaraouselInfo ref={ref} className={className}>
            {showLabel && items.length > 0 ? <StyH4>{label}</StyH4> : null}
            <Grow />
            {seeAllLink && !isMobile && (
              <Link as={seeAllLink} href={href} onClick={seeAllAnalytics}>
                <BoldP1 color={theme.colors.green}>SEE ALL</BoldP1>
              </Link>
            )}
          </CaraouselInfo>
        )}
        <CarouselWrapper gridColumn={gridColumn} ref={viewRef} $css={$css}>
          <CardContainer $showBottomGap={showBottomGap} pos={pos}>
            {finalItems}
          </CardContainer>
          {controlButtons()}
        </CarouselWrapper>
        {renderNavButtons()}
        {!seeAllLink ? null : (
          <SeeAll label="See All" onClick={onClickSeeAll} />
        )}
      </>
    );
  }
);

Carousel.displayName = 'Carousel';

Carousel.propTypes = {
  showInfo: PropTypes.bool,
  showBottomGap: PropTypes.bool,
  children: PropTypes.node.isRequired,
  label: PropTypes.string,
  seeAllLink: PropTypes.string,
  href: PropTypes.string,
  showControls: PropTypes.bool,
  numberOfItems: PropTypes.number,
  gridColumn: PropTypes.string,
  className: PropTypes.string,
  showNavButtons: PropTypes.bool,
  dataAnalytics: PropTypes.string,
  showLabel: PropTypes.bool,
  viewRef: PropTypes.shape({ current: PropTypes.oneOfType([PropTypes.any]) }),
  $css: PropTypes.string
};

Carousel.defaultProps = {
  showInfo: true,
  showBottomGap: true,
  label: null,
  seeAllLink: null,
  href: null,
  showControls: true,
  numberOfItems: 3,
  gridColumn: 'span 18',
  className: '',
  showNavButtons: false,
  dataAnalytics: '',
  showLabel: true,
  viewRef: null,
  $css: ''
};
export default Carousel;
